import { apiRequest, buildApiUrl, API_CONFIG } from '../utils/api';

/**
 * Servicio para la gestión de usuarios
 */
class UserService {
  /**
   * Obtiene la lista de usuarios del equipo
   * @returns {Promise<Array>} Lista de usuarios
   */
  static async getUsers() {
    try {
      const url = buildApiUrl(API_CONFIG.ENDPOINTS.USERS);
      const response = await apiRequest(url, {
        method: 'GET'
      });

      if (response.response?.content?.users) {
        const { users, limit } = response.response.content;

        return {
          success: true,
          users,
          limit: typeof limit === 'number' ? limit : null
        };
      }

      return {
        success: false,
        error: 'No se pudieron obtener los usuarios'
      };
    } catch (error) {
      console.error('Error en getUsers:', error);
      return {
        success: false,
        error: error.message || 'Error al obtener usuarios'
      };
    }
  }

  /**
   * Crea un nuevo usuario
   * @param {Object} userData - Datos del usuario a crear
   * @param {string} userData.name - Nombre del usuario
   * @param {string} userData.lastName - Apellido del usuario
   * @param {string} userData.email - Email del usuario
   * @returns {Promise<Object>} Resultado de la operación
   */
  static async createUser(userData) {
    try {
      const url = buildApiUrl(API_CONFIG.ENDPOINTS.USERS);
      const response = await apiRequest(url, {
        method: 'POST',
        body: JSON.stringify(userData)
      });

      if (response.response?.message === 'User created') {
        return {
          success: true,
          userId: response.response.content.id,
          message: 'Usuario creado exitosamente'
        };
      }

      return {
        success: false,
        error: 'No se pudo crear el usuario'
      };
    } catch (error) {
      console.error('Error en createUser:', error);
      return {
        success: false,
        error: error.message || 'Error al crear el usuario'
      };
    }
  }

  /**
   * Actualiza un usuario existente
   * @param {number} userId - ID del usuario a actualizar
   * @param {Object} userData - Datos del usuario a actualizar
   * @param {string} userData.name - Nombre del usuario
   * @param {string} userData.lastName - Apellido del usuario
   * @param {string} userData.status - Estado del usuario (active/inactive)
   * @returns {Promise<Object>} Resultado de la operación
   */
  static async updateUser(userId, userData) {
    try {
      const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.USERS)}/${userId}`;
      const response = await apiRequest(url, {
        method: 'PUT',
        body: JSON.stringify(userData)
      });

      if (response.response?.message === 'Users update successfully') {
        return {
          success: true,
          user: response.response.content.user,
          message: 'Usuario actualizado exitosamente'
        };
      }

      return {
        success: false,
        error: 'No se pudo actualizar el usuario'
      };
    } catch (error) {
      console.error('Error en updateUser:', error);
      return {
        success: false,
        error: error.message || 'Error al actualizar el usuario'
      };
    }
  }

  /**
   * Elimina un usuario
   * @param {number} userId - ID del usuario a eliminar
   * @returns {Promise<Object>} Resultado de la operación
   */
  static async deleteUser(userId) {
    try {
      const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.USERS)}/${userId}`;
      const response = await apiRequest(url, {
        method: 'DELETE'
      });

      if (response.response?.message === 'user delete successfully') {
        return {
          success: true,
          message: 'Usuario eliminado exitosamente'
        };
      }

      return {
        success: false,
        error: 'No se pudo eliminar el usuario'
      };
    } catch (error) {
      console.error('Error en deleteUser:', error);
      return {
        success: false,
        error: error.message || 'Error al eliminar el usuario'
      };
    }
  }

  /**
   * Valida los datos de un usuario
   * @param {Object} userData - Datos del usuario a validar
   * @returns {Object} Resultado de la validación
   */
  static validateUserData(userData) {
    const errors = [];

    if (!userData.name || userData.name.trim() === '') {
      errors.push('El nombre es requerido');
    }

    if (!userData.lastName || userData.lastName.trim() === '') {
      errors.push('El apellido es requerido');
    }

    if (!userData.email || userData.email.trim() === '') {
      errors.push('El correo electrónico es requerido');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userData.email)) {
        errors.push('El correo electrónico no es válido');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default UserService;

